/**
 * Purchase attribution for the My Account recommendation modules (GROW-397).
 *
 * The `purchase` items carry the module's `item_list_name` themselves, so a
 * report can credit a module from the purchase event alone — Mixpanel, fed from
 * the same dataLayer push, sees nothing else. Checkout runs pages (and, for
 * Stripe's redirect flow, a whole page load) after the add, so the add remembers
 * which module each SKU came from here and PurchaseTracker reads it back.
 *
 * localStorage rather than sessionStorage: a member may add an accessory today
 * and check out tomorrow. Every access is wrapped — storage can be disabled,
 * full or hold junk, and attribution must never break the add or the purchase.
 */

const STORAGE_KEY = "wh_reco_attribution";

/** How long after the add a purchase is still credited to the module. */
export const RECO_ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface AttributionEntry {
  listName: string;
  /** When the add happened, ms since the epoch. */
  at: number;
}

type AttributionStore = Record<string, AttributionEntry>;

function isEntry(value: unknown): value is AttributionEntry {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as AttributionEntry).listName === "string" &&
    typeof (value as AttributionEntry).at === "number"
  );
}

/** The stored entries still inside the window; expired and malformed ones dropped. */
function readFresh(now: number): AttributionStore {
  let parsed: unknown;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {};
  }

  const fresh: AttributionStore = {};
  for (const [sku, entry] of Object.entries(parsed)) {
    if (isEntry(entry) && now - entry.at <= RECO_ATTRIBUTION_MAX_AGE_MS) {
      fresh[sku] = entry;
    }
  }
  return fresh;
}

function write(store: AttributionStore): void {
  try {
    if (Object.keys(store).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
  } catch {
    // Storage unavailable or full — the purchase simply goes unattributed.
  }
}

/**
 * Remembers that these SKUs were added from `listName`. Pass both the product's
 * and the chosen version's SKU: which of the two an order line reports depends on
 * the product, so either may be what `purchase` has to match.
 */
export function rememberRecoAttribution(
  skus: Array<string | null | undefined>,
  listName: string,
  now: number = Date.now()
): void {
  const store = readFresh(now);
  for (const sku of skus) {
    if (sku) store[sku] = { listName, at: now };
  }
  write(store);
}

/**
 * Returns the list each of `skus` was added from, for those still remembered,
 * and forgets them: a purchase consumes its attribution, so a later order of the
 * same SKU is not credited to the module again.
 */
export function takeRecoAttribution(
  skus: string[],
  now: number = Date.now()
): Record<string, string> {
  const store = readFresh(now);
  const taken: Record<string, string> = {};
  for (const sku of skus) {
    const entry = sku ? store[sku] : undefined;
    if (entry) {
      taken[sku] = entry.listName;
      delete store[sku];
    }
  }
  write(store);
  return taken;
}
